import {defineStore} from 'pinia'
import type {SettingsInterface} from "~~/server/types/settings";

export const useSettingsStore = defineStore('settings', {
    state: () => ({
        _settings: {} as SettingsInterface,
        _loaded: false,
        _locale: null as string | null,
        _promise: null as Promise<void> | null,
        _promiseLocale: null as string | null,
        _publicLoaded: false,
        _publicPromise: null as Promise<void> | null,
    }),
    getters: {
        settings: s => s._settings,
        isLoaded: s => s._loaded,
        isPublicLoaded: s => s._publicLoaded,
    },
    actions: {
        async load() {
            const nuxtApp = useNuxtApp()
            const locale = String(nuxtApp.$i18n.locale.value || nuxtApp.$i18n.defaultLocale)
            if (this._loaded && this._locale === locale) return
            if (this._promise && this._promiseLocale === locale) return this._promise;
            const { $api } = nuxtApp

            const promise = (async () => {
                const res = await $api<SettingsInterface>('settings', { method: 'POST' })
                if (locale !== String(nuxtApp.$i18n.locale.value || nuxtApp.$i18n.defaultLocale)) return
                this._settings = {...this._settings,...res}
                this._loaded = true
                this._locale = locale
            })()
            this._promise = promise
            this._promiseLocale = locale

            try {
                await promise
            }
            finally {
                if (this._promise === promise) {
                    this._promise = null
                    this._promiseLocale = null
                }
            }
        },
        async loadPublic() {
            if (this._publicLoaded) return
            if (this._publicPromise) return this._publicPromise;
            const { $api } = useNuxtApp()

            this._publicPromise = (async () => {
                const res = await $api<SettingsInterface>('settings', { method: 'GET' })
                this._settings = {...this._settings,...res}
                this._publicLoaded = true
            })()

            try {
                await this._publicPromise
            }
            finally {
                this._publicPromise = null
            }
        },
        async loadPreview(designTemplateId: number | null) {
            const { $api } = useNuxtApp()
            const privateOptions = designTemplateId
                ? { method: 'POST' as const, body: { design_template_id: designTemplateId } }
                : { method: 'POST' as const }
            const publicOptions = designTemplateId
                ? { method: 'GET' as const, query: { design_template_id: designTemplateId } }
                : { method: 'GET' as const }

            const [privateSettings, publicSettings] = await Promise.all([
                $api<SettingsInterface>('settings', privateOptions),
                $api<SettingsInterface>('settings', publicOptions),
            ])

            this._settings = { ...privateSettings, ...publicSettings }
            this._loaded = true
            const nuxtApp = useNuxtApp()
            this._locale = String(nuxtApp.$i18n.locale.value || nuxtApp.$i18n.defaultLocale)
            this._publicLoaded = true
        },
    }

})
